import { useNavigate, useSearch } from "@tanstack/react-router";
import { useBillKpis, useBills } from "@/api/queries";
import type { BillSummary } from "@/api/types";
import { KpiStrip } from "@/components/KpiStrip";
import { PermittedAction } from "@/components/PermittedAction";
import type { Column } from "@/components/list/DataTable";
import { DocumentStatusBadge, PaymentStatusBadge } from "@/components/list/StatusBadge";
import type { ListSearch } from "@/components/list/listQuery";
import { ListScreen, listQueryFor } from "@/components/list/ListScreen";
import { formatDate } from "@/lib/dates";
import { formatAmount } from "@/lib/money";

export function BillsListPage() {
  const search = useSearch({ from: "/purchases/bills" }) as ListSearch;
  const navigate = useNavigate();
  const { data, isPending, error, refetch } = useBills(listQueryFor(search));
  const { data: kpis } = useBillKpis();

  const columns: Array<Column<BillSummary>> = [
    {
      key: "number",
      header: "Number",
      width: "13ch",
      numeric: true,
      render: (row) => row.number ?? "Draft",
    },
    { key: "supplier", header: "Supplier", render: (row) => row.supplierName },
    {
      key: "vendorRef",
      header: "Supplier's invoice",
      // The supplier's own number, not ours. Reconciling a statement means matching on theirs.
      render: (row) => <span className="num text-fg-muted text-xs">{row.vendorInvoiceNo}</span>,
    },
    { key: "date", header: "Date", width: "12ch", render: (row) => formatDate(row.date) },
    { key: "due", header: "Due", width: "12ch", render: (row) => formatDate(row.due) },
    {
      key: "status",
      header: "Status",
      width: "14ch",
      // An approved bill with nothing left owing has been paid, whichever way.
      render: (row) =>
        row.status === "approved" && Number(row.balance) === 0 ? (
          <PaymentStatusBadge status="paid" />
        ) : (
          <DocumentStatusBadge status={row.status} />
        ),
    },
    {
      key: "total",
      header: "Amount",
      align: "right",
      numeric: true,
      render: (row) => formatAmount(row.total),
    },
    {
      key: "balance",
      header: "Balance",
      align: "right",
      numeric: true,
      render: (row) => formatAmount(row.balance),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <KpiStrip
        items={(kpis ?? []).map((kpi) => ({
          label: kpi.label,
          value: formatAmount(kpi.value),
          detail: kpi.sub,
          tone: kpi.tone,
        }))}
        loading={kpis === undefined}
      />

      <ListScreen
        route="/purchases/bills"
        title="Bills"
        subtitle={data ? `${data.total} this fiscal year` : "Loading bills"}
        actions={
          <PermittedAction
            variant="secondary"
            leftIcon="Plus"
            permission="purchases.bills.create"
            onClick={() => void navigate({ to: "/purchases/bills/new" })}
          >
            New bill
          </PermittedAction>
        }
        columns={columns}
        rows={data?.items ?? []}
        total={data?.total ?? 0}
        hasNext={data?.next != null}
        loading={isPending}
        error={error}
        onRetry={() => void refetch()}
        rowKey={(row) => row.id}
        rowLabel={(row) =>
          `Bill ${row.number ?? "draft"}, ${row.supplierName}, ${formatAmount(row.total)}`
        }
        onRowClick={(row) => void navigate({ to: "/purchases/bills/$id", params: { id: row.id } })}
        searchPlaceholder="Search number, supplier or their invoice"
        empty={{
          icon: "FileInput",
          title: "No bills yet",
          description:
            "A bill records what a supplier has invoiced you. Recoverable tax is claimed; blocked tax is capitalised into the expense.",
        }}
        filteredEmpty={{
          icon: "Search",
          title: "No bills match these filters",
          description: "Try a wider date range, or clear the filters to see everything again.",
        }}
      />
    </div>
  );
}
